import type { ListItemButtonProps } from '@mui/material';
import { ListItemButton, ListItemText } from '@mui/material';
import type { RouteComponentProps } from '@reach/router';
import { navigate } from 'gatsby';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import Icon from '@components/Icon/Icon';

import {
  BLOOD_PRODUCTS,
  HEROIC_MEASURES,
  ORGAN_DONATION,
} from '@constants/routes';

import { useGetLegalDirectives } from '@hooks/api/legal-directive';

import { legalAcceptance } from '@utils/records';

const messages = defineMessages({
  title: {
    id: 'legalMain.title',
    defaultMessage: 'Legal/Directives',
  },
  livingWillPOA: {
    id: 'legalMain.livingWillPOA',
    defaultMessage: 'Living Will / Power of Attorney for Personal Care',
  },
  docsUploaded: {
    id: 'legalMain.docsUploaded',
    defaultMessage: 'documents uploaded',
  },
  docUploaded: {
    id: 'legalMain.docUploaded',
    defaultMessage: 'document uploaded',
  },
  contactsListed: {
    id: 'legalMain.contactsListed',
    defaultMessage: 'contacts listed',
  },
  contactListed: {
    id: 'legalMain.contactListed',
    defaultMessage: 'contact listed',
  },
  heroicMeasures: {
    id: 'legalMain.heroicMeasures',
    defaultMessage: 'Heroic Measures and Life Support',
  },
  noToAll: {
    id: 'legalMain.noToAll',
    defaultMessage: 'No to all',
  },
  organDonation: {
    id: 'legalMain.organDonation',
    defaultMessage: 'Organ Donation',
  },
  organsAndTissue: {
    id: 'legalMain.organsAndTissue',
    defaultMessage: 'Organs and Tissue',
  },
  yesToAll: {
    id: 'legalMain.yesToAll',
    defaultMessage: 'Yes to all',
  },
  bloodProducts: {
    id: 'legalMain.bloodProducts',
    defaultMessage: 'Blood Products',
  },
  noToSpecificBlood: {
    id: 'legalMain.noToSpecificBlood',
    defaultMessage: 'No to specific blood products',
  },
  noToSpecificHeroic: {
    id: 'legalMain.noToSpecificHeroic',
    defaultMessage: 'No to specific heroic measures',
  },
  yesToSpecific: {
    id: 'legalMain.yesToSpecific',
    defaultMessage: 'Yes to specific {type}',
  },
});

interface LegalListProps {
  hasError?: boolean;
}

const LegalList: React.FC<RouteComponentProps & LegalListProps> = ({
  hasError = false,
}) => {
  const { formatMessage } = useIntl();

  const { data: legalDirectivesData, loading: legalDirectivesLoading } =
    useGetLegalDirectives({ skip: hasError });
  const loading = legalDirectivesLoading;

  const heroicDirective = legalDirectivesData.find((d) => d.isHeroicMeasures());
  const organDirective = legalDirectivesData.find((d) => d.isOrganDonation());
  const bloodDirective = legalDirectivesData.find((d) => d.isBloodProducts());

  const heroicAcceptance = legalAcceptance('heroic-measures', heroicDirective);
  const bloodAcceptance = legalAcceptance('blood-products', bloodDirective);
  const organsAcceptance = legalAcceptance('specific-organs', organDirective);

  let heroicAcceptanceMsg = '';
  let bloodAcceptanceMsg = '';
  let organsAcceptanceMsg = '';
  if (heroicAcceptance.acceptsAll) {
    heroicAcceptanceMsg = formatMessage(messages.yesToAll);
  } else if (heroicAcceptance.acceptsNone) {
    heroicAcceptanceMsg = formatMessage(messages.noToAll);
  } else if (heroicAcceptance.acceptsSome) {
    heroicAcceptanceMsg = formatMessage(messages.yesToSpecific, {
      type: formatMessage(messages.heroicMeasures).toLowerCase(),
    });
  }
  if (bloodAcceptance.acceptsAll) {
    bloodAcceptanceMsg = formatMessage(messages.yesToAll);
  } else if (bloodAcceptance.acceptsNone) {
    bloodAcceptanceMsg = formatMessage(messages.noToAll);
  } else if (bloodAcceptance.acceptsSome) {
    bloodAcceptanceMsg = formatMessage(messages.yesToSpecific, {
      type: formatMessage(messages.bloodProducts).toLowerCase(),
    });
  }
  if (organsAcceptance.acceptsAll) {
    organsAcceptanceMsg = formatMessage(messages.yesToAll);
  } else if (organsAcceptance.acceptsNone) {
    organsAcceptanceMsg = formatMessage(messages.noToAll);
  } else if (organsAcceptance.acceptsSome) {
    organsAcceptanceMsg = formatMessage(messages.yesToSpecific, {
      type: formatMessage(messages.organsAndTissue).toLowerCase(),
    });
  }

  const listItemButtonProps: ListItemButtonProps = {
    divider: true,
  };
  const secondaryAction = (
    <Icon color="text.primary" name="chevron-down" rotation={-90} type="fas" />
  );

  return (
    <ContentLoader loading={loading}>
      <ListItemButton
        {...listItemButtonProps}
        onClick={() => {
          navigate(HEROIC_MEASURES);
        }}
      >
        <ListItemText
          primary={formatMessage(messages.heroicMeasures)}
          secondary={hasError ? null : heroicAcceptanceMsg}
        />
        {secondaryAction}
      </ListItemButton>

      <ListItemButton
        {...listItemButtonProps}
        onClick={() => {
          navigate(ORGAN_DONATION);
        }}
      >
        <ListItemText
          primary={formatMessage(messages.organDonation)}
          secondary={hasError ? null : organsAcceptanceMsg}
        />
        {secondaryAction}
      </ListItemButton>

      <ListItemButton
        onClick={() => {
          navigate(BLOOD_PRODUCTS);
        }}
      >
        <ListItemText
          primary={formatMessage(messages.bloodProducts)}
          secondary={hasError ? null : bloodAcceptanceMsg}
        />
        {secondaryAction}
      </ListItemButton>
    </ContentLoader>
  );
};

export default withErrorBoundary(LegalList, {
  fallback: <LegalList hasError />,
});
